import React from 'react';
import classNames from 'classnames/bind';
import {
  Box,
  Button,
  CloudinaryImage,
  Modal,
  RichText,
  Text,
} from '@stitch-fix/mode-react';
import { useSelectEvent, useViewEvent } from '@stitch-fix/event-reporter';
import styles from './style.module.scss';
import { useGetAddProfileIngressModalQuery } from './getAddProfileModal';
import { getWindow } from '../../../../../../../helpers/getBrowserGlobals';

const cx = classNames.bind(styles);

export interface AddProfileModalProps {
  isModalOpen: boolean;
  onClose: () => void;
}

const AddProfileModal = ({ isModalOpen, onClose }: AddProfileModalProps) => {
  const { data, loading, error } = useGetAddProfileIngressModalQuery({
    skip: !isModalOpen,
  });

  const modalData = data?.client?.addProfileIngressModal;

  const constructUrlWithReturnPath = (baseUrl: string) => {
    const win = getWindow();
    const currentUrl = win?.location?.href || '/home';
    const url = new URL(baseUrl);
    url.searchParams.set('ret', currentUrl);
    return url.toString();
  };

  const { reportSelectEvent } = useSelectEvent();
  const { EventProvider, ref } = useViewEvent({
    schema: 'view',
    name: 'household_ingress',
  });

  const handleButtonClick = () => {
    reportSelectEvent({
      schema: 'select',
      name: 'household_ingress',
      action_name: 'navigate_to_profile_business_line',
    });
    onClose();
  };

  const renderErrorState = () => {
    return (
      <Box my={2}>
        <Text color="blue-45" setting="body-medium" mb={0.5}>
          Oops! Something went wrong.
        </Text>
        <Text color="blue-45" setting="body-medium">
          Please try again.
        </Text>
      </Box>
    );
  };

  const renderFooter = () => {
    return !loading && !error && modalData && modalData?.buttonCta?.url ? (
      <Button
        as="a"
        variant="filled-statement"
        mt={0.25}
        onClick={handleButtonClick}
        href={constructUrlWithReturnPath(modalData.buttonCta.url)}
        width="fill"
      >
        {modalData?.buttonCta?.label?.text}
      </Button>
    ) : null;
  };

  const renderModalContents = () => {
    if (loading) {
      return null;
    }

    if (error) {
      return renderErrorState();
    }

    if (!modalData) {
      return null;
    }

    return (
      <EventProvider>
        <Box ref={ref}>
          <Text
            color="blue-45"
            setting="display-medium"
            mb={{ sm: 1.5, lg: 0.75 }}
            mt={{ sm: 0.75, lg: 0 }}
            style={{ textWrap: 'pretty' }}
          >
            <RichText>{String(modalData?.title?.formatted)}</RichText>
          </Text>
          <Box bgColor="gray-99" pt={1} pb={0.5} px={1}>
            <ul className={cx('list')}>
              {modalData?.body.map(item => (
                <li key={item.substring(0, 15).replace(/\s+/g, '-')}>
                  <Text color="blue-45" setting="body-medium" mb={0.5}>
                    {item}
                  </Text>
                </li>
              ))}
            </ul>
          </Box>
        </Box>
      </EventProvider>
    );
  };

  const renderModalImage = () => {
    if (loading || error || !modalData) return null;

    return (
      <Box className={cx('image')}>
        <CloudinaryImage
          baseUrl="https://d3n78nkjl8tizo.cloudfront.net/stitch-fix/image/upload/"
          mediaPath={modalData?.media?.mediaPath}
          sizes={{ sm: 560 }}
          alt={modalData?.media?.altText}
        />
      </Box>
    );
  };

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={onClose}
      hero={renderModalImage()}
      variant="slide-up-small"
      hAlign="center"
      bodyLock="never"
      footer={renderFooter()}
      ariaLabel="Add a profile"
    >
      {renderModalContents()}
    </Modal>
  );
};

export default AddProfileModal;
